// Nether biomes (GEN-10 extension). PURE + deterministic (ENG-1/ENG-3).
//
// The nether wastes generator shapes one rock body for the whole dimension; this
// module says what that rock is *made of* where you stand. Two salted 2D noise
// fields (a "heat" and a "weirdness" axis, the nether's analogue of the overworld
// temperature/humidity pair) pick one of five biomes per column, and each biome
// names the blocks its floor is painted with. Terrain shape is deliberately
// untouched — the same caverns and lava sea, dressed differently — so the biome
// layer is a pure surface concern and the M7 floor/ceiling fields stay exact.
import { NETHER_BIOME } from '../../config';
import { defineSalt } from '../../engine/rng';
import { Simplex, fbm2 } from '../../engine/noise';

export const NETHER_BIOMES = [
  'nether_wastes', 'crimson_forest', 'warped_forest', 'soul_sand_valley', 'basalt_deltas',
] as const;
export type NetherBiome = (typeof NETHER_BIOMES)[number];

const S_HEAT = defineSalt('nether-biome-heat');
const S_WEIRD = defineSalt('nether-biome-weird');

/** The floor materials one nether biome paints: its exposed top block and the
 *  filler just beneath. `null` ⇒ leave the generator's netherrack alone. */
export interface NetherSurface {
  top: string | null;
  filler: string | null;
  /** Filler depth in blocks under the top cell. */
  depth: number;
}

export const NETHER_SURFACES: Readonly<Record<NetherBiome, NetherSurface>> = Object.freeze({
  nether_wastes: { top: null, filler: null, depth: 0 },
  crimson_forest: { top: 'crimson_nylium', filler: null, depth: 0 },
  warped_forest: { top: 'warped_nylium', filler: null, depth: 0 },
  soul_sand_valley: { top: 'soul_sand', filler: 'soul_soil', depth: 3 },
  basalt_deltas: { top: 'basalt', filler: 'blackstone', depth: 3 },
});

/**
 * Per-column nether biome from two salted noise fields. The mapping is a small
 * fixed decision tree rather than data-driven rules: the nether has five biomes
 * and no climate spline, so a table would be indirection without payoff.
 *
 * Layout: warped forest sits at the cold end, crimson at the hot end, soul sand
 * valley in the cold-weird corner and basalt deltas in the hot-weird corner, with
 * the wastes filling the broad middle (as in Minecraft, where the wastes are by
 * far the most common biome).
 */
export class NetherBiomeSampler {
  private readonly heat: Simplex;
  private readonly weird: Simplex;

  constructor(seed: number | string | bigint) {
    this.heat = new Simplex(`${seed}#${S_HEAT}`);
    this.weird = new Simplex(`${seed}#${S_WEIRD}`);
  }

  /** The raw [-1,1]-ish field pair at a column (exposed for tests/tuning). */
  sample(x: number, z: number): { heat: number; weird: number } {
    return {
      heat: fbm2(this.heat, x, z, { frequency: NETHER_BIOME.FREQ, octaves: 2 }),
      weird: fbm2(this.weird, x, z, { frequency: NETHER_BIOME.FREQ, octaves: 2 }),
    };
  }

  biomeAt(x: number, z: number): NetherBiome {
    return selectNetherBiome(this.sample(x, z));
  }
}

/** The flora a fungal-forest biome grows on its nylium: the huge fungus (stem +
 *  wart-block canopy + shroomlight pores) and the small ground cover. */
export interface NetherFlora {
  stem: string;
  wart: string;
  fungus: string;
  roots: string;
  /** The vine this forest grows. Which WAY it runs is the vine's own business
   *  (`VINES` in game/vines.ts) — crimson weeps down off the canopy and the roof,
   *  warped twists up out of the nylium — so this is just a name. */
  vine?: string;
  /** A second ground cover beyond roots (the warped forest's nether sprouts). */
  sprouts?: string;
}

export const NETHER_FLORA: Readonly<Partial<Record<NetherBiome, NetherFlora>>> = Object.freeze({
  crimson_forest: {
    stem: 'crimson_stem', wart: 'nether_wart_block', fungus: 'crimson_fungus', roots: 'crimson_roots',
    vine: 'weeping_vines',
  },
  warped_forest: {
    stem: 'warped_stem', wart: 'warped_wart_block', fungus: 'warped_fungus', roots: 'warped_roots',
    vine: 'twisting_vines', sprouts: 'nether_sprouts',
  },
});

/** Pure field-pair → biome mapping (the tree the sampler applies). */
export function selectNetherBiome({ heat, weird }: { heat: number; weird: number }): NetherBiome {
  const T = NETHER_BIOME.THRESHOLD;
  if (weird > T.WEIRD) return heat > 0 ? 'basalt_deltas' : 'soul_sand_valley';
  if (heat > T.HOT) return 'crimson_forest';
  if (heat < T.COLD) return 'warped_forest';
  return 'nether_wastes';
}
